/**
 * Embedded games can push their own session-history entries from inside the
 * iframe (Number Smash pushes one per level). Those entries join the top-level
 * joint session history but never change the parent URL, so a single
 * `history.back()` from the overlay only rewinds the game.
 *
 * The parent cannot see how many iframe entries exist or where the user is
 * among them (`history.length` caps at 50 and does not move when the user
 * presses Back inside the game). Instead:
 *
 * 1. Navigation API: traverse straight to the parent frame's previous entry.
 *    The browser skips every iframe entry in between, wherever the user is.
 * 2. Fallback: step back one entry at a time until the parent URL changes.
 *    Slower, but it stops on the first parent entry and cannot overshoot.
 */

const FALLBACK_STEP_MS = 250;

interface NavigationEntryLike {
  key: string;
  index: number;
}

interface NavigationLike {
  currentEntry: NavigationEntryLike | null;
  entries(): NavigationEntryLike[];
  traverseTo(key: string): { committed: Promise<unknown>; finished: Promise<unknown> };
}

interface LeaveGameOverlayOptions {
  back: () => void;
  win?: Window;
}

function getNavigationApi(win: Window): NavigationLike | null {
  const nav = (win as Window & { navigation?: NavigationLike }).navigation;
  if (!nav || typeof nav.traverseTo !== "function" || typeof nav.entries !== "function") {
    return null;
  }
  return nav;
}

function stepBackUntilParentLeaves(win: Window, back: () => void): Promise<void> {
  const startHref = win.location.href;
  const maxSteps = Math.max(1, win.history.length);
  let steps = 0;

  return new Promise((resolve) => {
    const step = () => {
      if (win.location.href !== startHref || steps >= maxSteps) {
        resolve();
        return;
      }
      steps++;
      back();
      win.setTimeout(step, FALLBACK_STEP_MS);
    };
    step();
  });
}

/** Leave the overlay's history entry, skipping any entries the embedded game pushed. */
export async function leaveGameOverlay({ back, win = window }: LeaveGameOverlayOptions) {
  const nav = getNavigationApi(win);
  const current = nav?.currentEntry;
  const previous = current
    ? nav?.entries().find((entry) => entry.index === current.index - 1)
    : null;

  if (!nav || !current || !previous) {
    await stepBackUntilParentLeaves(win, back);
    return;
  }

  const { committed, finished } = nav.traverseTo(previous.key);
  committed.catch(() => undefined);
  try {
    await finished;
  } catch {
    // Interrupted by another navigation: that navigation wins.
    if (nav.currentEntry?.key === current.key) {
      await stepBackUntilParentLeaves(win, back);
    }
  }
}
